/**
 * Jovii — WHICH TILE THE BROWSE LAYER IS LOOKING AT.
 *
 * The arithmetic behind `focus.svelte.ts`, in a plain module with no runes in
 * it, because "does the focus jump about?" is a question about numbers and a
 * question about numbers should be answerable without a phone in your hand.
 *
 * See `focus.svelte.ts` for what focus MEANS and why it means two different
 * things under a pointer and under a thumb.
 *
 * ── IT IS THE LEFTMOST ONE, NOT THE MIDDLE ONE ──────────────────────────────
 *
 * This used to answer "whatever is nearest the centre of the screen", which is
 * a defensible reading of "what are you looking at" and turned out to be the
 * wrong one. A rail is read from the left, the first card of a row is the one a
 * shelf is designed around, and — the part that decided it — a card that grows
 * into a 16:9 trailer has to grow SOMEWHERE. Growing rightward from the
 * leftmost visible card is the only direction with guaranteed room in it: there
 * is a whole viewport to its right, so nothing can be pushed off the screen and
 * no row has to reflow to make space. A centred card would have half a screen
 * either side and would have to choose.
 *
 * So: the row nearest the middle of the screen vertically, and then the
 * leftmost card in that row that is really on screen.
 *
 * ── SETTLED, NOT JUMPY ──────────────────────────────────────────────────────
 *
 * Two thresholds rather than one, which is what stops the answer flickering
 * while a rail slides:
 *
 *   • A challenger must be `TAKE_VISIBLE` on screen before it can take the
 *     focus.
 *   • The incumbent keeps it until it is less than `HOLD_VISIBLE` on screen.
 *
 * The gap between them is real hysteresis measured in pixels of scroll — on a
 * 118 px card a challenger needs 78 px showing and the incumbent gives up at
 * 47 px, so 31 px of movement separates the two events and nothing can trade
 * the focus back and forth on jitter. The vertical term has hysteresis of its
 * own (`HYSTERESIS_PX`) so the focus does not hop between rails as the page
 * moves.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * The arithmetic, kept pure and testable
 * ──────────────────────────────────────────────────────────────────────────── */

/** One candidate's rectangle, in viewport coordinates. */
export interface FocusRect {
  id: string;
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/** How much nearer the middle another ROW must be before it takes the focus. */
export const HYSTERESIS_PX = 48;

/** How long scrolling has to be quiet before the answer is re-read. */
export const SETTLE_MS = 90;

/**
 * How much of a candidate must be on screen — on BOTH axes — before it may
 * take the focus. Two thirds: a card half off the side of a rail is not the one
 * you are looking at, however far left it happens to be.
 */
export const TAKE_VISIBLE = 0.66;

/**
 * How little of the focused card may remain before it gives the focus up. The
 * distance between this and `TAKE_VISIBLE` is the hysteresis; without it a card
 * hovering on the threshold would hand the focus over and take it back on every
 * pixel of scroll, and the trailer dwell would restart each time.
 */
export const HOLD_VISIBLE = 0.4;

/** What fraction of `r` is inside 0…`extent`. */
function visibleFraction(from: number, to: number, extent: number): number {
  const size = to - from;
  if (size <= 0) return 0;
  const shown = Math.min(to, extent) - Math.max(from, 0);
  return shown <= 0 ? 0 : shown / size;
}

function onScreenEnough(r: FocusRect, view: Viewport, floor: number): boolean {
  return (
    visibleFraction(r.top, r.bottom, view.height) >= floor &&
    visibleFraction(r.left, r.right, view.width) >= floor
  );
}

/** Distance from the middle of the screen to the middle of this rectangle. */
function rowDistance(r: FocusRect, view: Viewport): number {
  return Math.abs((r.top + r.bottom) / 2 - view.height / 2);
}

/**
 * THE LEFTMOST THING ON SCREEN, in the row nearest the middle of it — or null
 * when nothing qualifies.
 *
 * `current` is the incumbent, and it is not a tie-break: it is held to the
 * looser `HOLD_VISIBLE` floor and its row is kept unless another row is
 * `hysteresis` px nearer the middle. Both of those are what make a slow scroll
 * change the answer once rather than four times.
 */
export function leftmostId(
  rects: readonly FocusRect[],
  view: Viewport,
  current: string | null,
  hysteresis: number = HYSTERESIS_PX
): string | null {
  const incumbent = current === null ? null : (rects.find((r) => r.id === current) ?? null);
  const held =
    incumbent !== null && onScreenEnough(incumbent, view, HOLD_VISIBLE) ? incumbent : null;

  const runners = rects.filter((r) => r.bottom > r.top && onScreenEnough(r, view, TAKE_VISIBLE));
  if (runners.length === 0) return held === null ? null : held.id;

  /* Which ROW. The nearest row wins, but the row the focus is already in keeps
     it until another is clearly nearer — otherwise a page scrolled by a thumb
     drags the focus down through every rail it passes. */
  let anchor = runners[0]!;
  let best = rowDistance(anchor, view);
  for (const r of runners) {
    const d = rowDistance(r, view);
    if (d < best) {
      best = d;
      anchor = r;
    }
  }
  if (held !== null && rowDistance(held, view) - hysteresis <= best) anchor = held;

  /* Which CARD in that row. Rows are horizontal, so "same row" is a vertical
     overlap of more than half a card — cards in one rail share a top edge, and
     a rail half a screen away cannot possibly qualify. */
  const middle = (anchor.top + anchor.bottom) / 2;
  const reach = (anchor.bottom - anchor.top) / 2;
  let winner = anchor;
  for (const r of runners) {
    if (Math.abs((r.top + r.bottom) / 2 - middle) > reach) continue;
    if (r.left < winner.left) winner = r;
  }

  /* The incumbent keeps it while it is in the chosen row and still at least as
     far left as the challenger. Growing the visible test into a strict "further
     left" test is what stops a card that is fractionally left of the incumbent
     — a sub-pixel layout difference, a shadow, a rounding — taking it. */
  if (held !== null && Math.abs((held.top + held.bottom) / 2 - middle) <= reach) {
    if (held.left <= winner.left + 1) return held.id;
  }
  return winner.id;
}

/* ────────────────────────────────────────────────────────────────────────────
 * A tap on a card — focus first, navigate second
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * WHAT A CLICK ON A TILE SHOULD DO, decided per EVENT and never per device.
 *
 * With a mouse, hovering a card already focused it, so a click means "open it".
 * Under a thumb there is no hover: the first tap is the hover — it focuses the
 * card and opens the details strip — and only a tap on the card that is ALREADY
 * focused navigates. Without this, browsing on a phone is a minefield: every
 * tap while reading a rail is a page navigation.
 *
 * Per event, because per device is wrong on real hardware: an iPad grows a
 * trackpad in the afternoon and its clicks must navigate like a mouse's, while
 * a touch on a touchscreen laptop must behave like a phone's. The evidence is
 * taken in this order:
 *
 *   1. The `pointerType` of the `pointerdown` that began THIS gesture. It
 *      outranks the click's own label, because the click's label lies: WebKit
 *      synthesises a tap's `click` from its compatibility MOUSE events, so on
 *      an iPad the click arrives as a PointerEvent claiming `pointerType:
 *      'mouse'` — measured in Playwright WebKit 26.5, where a touch tap runs
 *      pointerdown(pt=touch) … mousedown … click(PointerEvent, pt=MOUSE),
 *      while Chromium's click honestly says 'touch'. Trusting the click first
 *      made every first tap on a real iPad navigate; the pointerdown is the
 *      only witness that saw the actual finger. A trackpad click cannot be
 *      misjudged by this: its own pointerdown says 'mouse' (staleness — a
 *      pointerdown from an EARLIER gesture — is the caller's job to filter,
 *      and Poster.svelte both time-bounds and consumes the note per click).
 *   2. The click's own `pointerType`, when no pointerdown was seen — Chromium
 *      and Firefox label it truthfully.
 *   3. Only when there is no pointer evidence at all: `hover: none` measured AT
 *      EVENT TIME, and only for a real gesture (`detail > 0`). A keyboard or
 *      d-pad activation has `detail === 0` and must never be intercepted —
 *      Enter on a television is a deliberate "open it".
 *
 * `focused` is the card's state WHEN THE GESTURE BEGAN, not when the click
 * lands: on some hardware a touch fires `pointerenter` before `pointerdown`,
 * and a card that focused itself mid-gesture has not been "tapped twice".
 *
 * `moving` covers the momentum-scroll tap: most browsers suppress the click a
 * finger uses to stop a fling, but not all do, and a click that arrives while
 * the page is still moving is a brake, not a choice — it must never navigate.
 */
export interface TapContext {
  /** `pointerType` off the click event itself, where it is a PointerEvent. */
  pointerType?: string | undefined;
  /** `pointerType` of the pointerdown that began this gesture, if one was seen. */
  downPointerType?: string | null | undefined;
  /** `matchMedia('(hover: none)')` measured when the event fired. */
  hoverNone: boolean;
  /** The click's `detail` — 0 for keyboard/d-pad activation, ≥1 for a gesture. */
  detail: number;
  /** Was THIS card focused when the gesture began? */
  focused: boolean;
  /** Is the page scrolling right now (`focusState.moving`)? */
  moving: boolean;
}

export type TapVerdict = 'navigate' | 'focus' | 'ignore';

export function tapVerdict(t: TapContext): TapVerdict {
  const down = t.downPointerType ?? null;
  const clickType = t.pointerType === undefined || t.pointerType === '' ? null : t.pointerType;
  /*
   * The gesture's own pointerdown first, the click's label second, the media
   * query last — see the evidence order above. The invariant this encodes: a
   * gesture that BEGAN as a touch is a touch, whatever the browser fans out of
   * the tap afterwards.
   */
  const touch =
    down === 'touch' ||
    (down === null && (clickType === 'touch' || (clickType === null && t.hoverNone && t.detail > 0)));
  if (!touch) return 'navigate';
  if (t.moving) return t.focused ? 'ignore' : 'focus';
  return t.focused ? 'navigate' : 'focus';
}

/**
 * Whether a deliberate tap's focus still stands. The ambient "leftmost card"
 * answer takes over only once the tapped card has substantially left the
 * screen — the same `HOLD_VISIBLE` floor the incumbent is held to, so a tap
 * and a scroll argue by one rule.
 */
export function pressStands(r: FocusRect, view: Viewport): boolean {
  return onScreenEnough(r, view, HOLD_VISIBLE);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The settle nudge — a focused tile never RESTS half off the screen
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * HOW FAR A RAIL MUST MOVE so its focused tile is wholly on the glass, in px
 * of `scrollBy` — 0 when it already is.
 *
 * A swipe and a half leaves the rail settled with a tile straddling the left
 * edge, and the focus arithmetic is ALLOWED to choose that tile: a challenger
 * needs only `TAKE_VISIBLE` showing and an incumbent holds to `HOLD_VISIBLE`,
 * because during a scroll those floors are what keep the answer settled
 * rather than jumpy. At REST they leave the details strip pointing at a card
 * you cannot fully see. The floors are right and the resting place is wrong,
 * so the fix is neither loosening one nor re-aiming the focus (a hand-over at
 * settle is exactly the flicker the hysteresis exists to prevent) — it is the
 * rail finishing the job: once the scroll has settled, nudge the track so the
 * chosen tile stands clear.
 *
 * `pad` is the scroller's own `scroll-padding-inline-start` — the nudge lands
 * the tile where a completed snap would have put it, so it reads as the snap
 * catching up, not as a second gesture. A left clip outranks a right clip
 * (rails are read from the left, and the leftmost-tile rule means a right
 * clip only happens to a card wider than the space that is left): the delta
 * for a right clip is capped so the left edge never goes under the pad.
 */
export function settleNudge(
  span: { left: number; right: number },
  viewportWidth: number,
  pad: number
): number {
  if (span.left < pad) return span.left - pad;
  const overflow = span.right - (viewportWidth - pad);
  if (overflow > 0) return Math.min(overflow, span.left - pad);
  return 0;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The ranked numeral's slide — as far as the designed reveal, never off the
 * glass
 * ──────────────────────────────────────────────────────────────────────────── */

/** How much of a ranked tile's numeral the focus slide is meant to reveal. */
export const NUMERAL_REVEAL = 0.82;

/** Breathing room kept between a slid-out numeral and the viewport's edge. */
export const NUMERAL_EDGE_INSET = 2;

/**
 * HOW FAR A FOCUSED RANKED TILE'S NUMERAL MAY SLIDE OUT, in px of leftward
 * travel.
 *
 * The designed reveal is `NUMERAL_REVEAL` of the numeral's width showing
 * beside the poster, which the slide reaches by travelling `0.82·W − peek`
 * leftward — a FIXED distance, and fixing it was the bug: focus lands on
 * tiles that sit AT the rail's left snap position (the ambient answer IS the
 * leftmost tile, parked `--page-pad` = 14px from the glass on a phone; an
 * open card re-snaps there too — measured, rank 10's column start went
 * 218 → 14.4 the moment its trailer opened at 390×844). From x=14 a
 * two-digit numeral's 48.3px slide puts half of the "10" past the viewport's
 * left edge, where the track clips it. The owner watched exactly that on an
 * iPhone.
 *
 * So the slide is CLAMPED to the room the tile actually has: never past
 * `NUMERAL_EDGE_INSET` from the glass, and never negative — a tile whose own
 * left edge is off screen keeps its numeral tucked at the peek, clipped no
 * worse than the tile itself is. `restLeft` is the numeral's UNTRANSFORMED
 * left edge in viewport coordinates, because the clamp is against the glass
 * and only the caller can see where the glass is.
 */
export function numeralSlide(opts: {
  /** The numeral's own width (`--rank-w`). */
  width: number;
  /** The sliver already showing beside the poster (`--rank-peek`). */
  peek: number;
  /** The numeral's untransformed left edge, in viewport coordinates. */
  restLeft: number;
}): number {
  const designed = opts.width * NUMERAL_REVEAL - opts.peek;
  const room = opts.restLeft - NUMERAL_EDGE_INSET;
  return Math.max(0, Math.min(designed, room));
}
